import { useState, useCallback, useMemo } from 'react'
import { Plus, ClipboardList, Pencil, CheckCircle2, LayoutGrid, List, XCircle, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { DataTable } from '@/components/data/DataTable'
import { ScenarioFormDialog } from '../components/ScenarioFormDialog'
import { EstatePartsDialog } from '../components/EstatePartsDialog'
import { useRecoveryScenarios } from '../hooks'
import { createScenarioColumns } from '../columns-scenarios'
import type { RecoveryScenario } from '../api'

export function RecoveryScenariosPage() {
    const { t } = useTranslation()
    const [selectedScenario, setSelectedScenario] = useState<RecoveryScenario | null>(null)
    const [showForm, setShowForm] = useState(false)
    const [view, setView] = useState<'cards' | 'table'>('cards')
    const [showParts, setShowParts] = useState(false)
    const destination = (to: string, location?: string | null) => ({
        in_place: t('recoveryScenarios.parts.inPlace', 'In place'),
        location: location || t('recoveryScenarios.notSpecified', 'Not specified'),
        cloud: t('recoveryScenarios.parts.cloudShort', 'Cloud'),
        vendor: t('recoveryScenarios.parts.vendorShort', 'Vendor'),
        not_recovered: t('recoveryScenarios.parts.notRecoveredShort', 'Not recovered'),
    } as Record<string, string>)[to] ?? to

    const { data: scenarios = [] } = useRecoveryScenarios()

    const handleCreate = useCallback(() => {
        setSelectedScenario(null)
        setShowForm(true)
    }, [])

    const handleEdit = useCallback((scenario: RecoveryScenario) => {
        setSelectedScenario(scenario)
        setShowForm(true)
    }, [])

    const columns = useMemo(() => createScenarioColumns(handleEdit, t), [handleEdit, t])

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <ClipboardList className="h-7 w-7 text-muted-foreground" />
                        <h1 className="text-3xl font-bold">
                            {t('recoveryScenarios.title', 'Recovery Scenarios')}
                        </h1>
                    </div>
                    <p className="text-muted-foreground mt-1">
                        {t('recoveryScenarios.pageDescription', 'Compare recovery scenarios and when to use each')}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowParts(true)} data-testid="open-estate-parts">
                        <Layers className="mr-2 h-4 w-4" />
                        {t('recoveryScenarios.parts.manageTitle', 'Parts of the estate')}
                    </Button>
                    <Button onClick={handleCreate}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('recoveryScenarios.createScenario', 'Add Scenario')}
                    </Button>
                </div>
            </div>

            <Tabs value={view} onValueChange={(v) => setView(v as 'cards' | 'table')} className="space-y-4">
                <TabsList>
                    <TabsTrigger value="cards" className="gap-1.5">
                        <LayoutGrid className="h-4 w-4" />
                        {t('recoveryScenarios.cardsView', 'Cards')}
                    </TabsTrigger>
                    <TabsTrigger value="table" className="gap-1.5">
                        <List className="h-4 w-4" />
                        {t('recoveryScenarios.tableView', 'Table')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="cards" className="m-0">
                    {scenarios.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
                            {t('recoveryScenarios.empty', 'No recovery scenarios yet — use Add Scenario to create one.')}
                        </div>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {scenarios.map((s) => (
                                <Card key={s.id} className={cn('flex flex-col', s.isDefault && 'border-primary/50')}>
                                    <CardHeader className="pb-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <CardTitle className="text-base leading-tight">{s.name || '—'}</CardTitle>
                                            <span className="flex shrink-0 gap-1">
                                                {s.isProduction && (
                                                    <Badge variant="outline" className="border-sky-600/60 text-sky-800 dark:text-sky-300" data-testid={`scenario-production-${s.id}`}>{t('recoveryScenarios.productionBadge', 'Production')}</Badge>
                                                )}
                                                {s.isDefault && (
                                                    <Badge variant="secondary">{t('recoveryScenarios.defaultBadge', 'Default')}</Badge>
                                                )}
                                            </span>
                                        </div>
                                        {s.description && (
                                            <CardDescription className="mt-1.5">{s.description}</CardDescription>
                                        )}
                                    </CardHeader>
                                    <CardContent className="flex-1">
                                        <div className="rounded-md border bg-muted/30 p-3">
                                            <div className="flex items-center gap-1.5 text-sm font-semibold">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                {t('recoveryScenarios.useWhen', 'Use when')}
                                            </div>
                                            <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                                                {s.useCase || t('recoveryScenarios.notSpecified', 'Not specified')}
                                            </p>
                                        </div>
                                        {s.whenNotToUse && (
                                            <div className="mt-2 rounded-md border bg-muted/30 p-3">
                                                <div className="flex items-center gap-1.5 text-sm font-semibold">
                                                    <XCircle className="h-4 w-4 text-red-600" />
                                                    {t('recoveryScenarios.dontUseWhen', 'Do not use when')}
                                                </div>
                                                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{s.whenNotToUse}</p>
                                            </div>
                                        )}
                                        <dl className="mt-3 space-y-1 text-sm">
                                            {(s.plan?.parts.length ?? 0) > 0 && (
                                                <div data-testid={`scenario-plan-${s.id}`}>
                                                    <ul className="mt-1 space-y-0.5 rounded-md border p-2 text-xs">
                                                        {s.plan!.parts.map((p) => (
                                                            <li key={p.partId} className="flex justify-between gap-2">
                                                                <span className="text-muted-foreground">{p.partName}</span>
                                                                <span className="text-right font-medium">
                                                                    {destination(p.recoversTo, p.locationName)}
                                                                    {p.fallbackTo && <span className="font-normal text-muted-foreground"> {t('recoveryScenarios.parts.elseShort', 'else {{where}}', { where: destination(p.fallbackTo, p.fallbackLocationName) })}</span>}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    {s.plan!.clouds.length > 0 && (
                                                        <p className="mt-1 text-xs text-muted-foreground">{t('recoveryScenarios.parts.cloudsShort', 'Clouds: {{list}}', { list: s.plan!.clouds.map((c) => c.name).join(', ') })}</p>
                                                    )}
                                                </div>
                                            )}
                                            {(s.dependsOn?.length ?? 0) > 0 && (
                                                <div className="flex gap-2" data-testid={`scenario-depends-${s.id}`}>
                                                    <dt className="text-muted-foreground">{t('recoveryScenarios.dependsOn.title', 'Depends on')}:</dt>
                                                    <dd className="font-medium">{s.dependsOn!.map((d) => d.name).join(', ')}</dd>
                                                </div>
                                            )}
                                            {(s.createdByName || s.updatedByName) && (
                                                <div className="flex gap-2 text-xs" data-testid={`scenario-who-${s.id}`}>
                                                    <dd className="text-muted-foreground">
                                                        {[s.createdByName && t('recoveryScenarios.addedBy', 'Added by {{name}}', { name: s.createdByName }),
                                                          s.updatedByName && t('recoveryScenarios.changedBy', 'Last changed by {{name}} on {{when}}', { name: s.updatedByName, when: s.updatedAt ? formatDateTime(s.updatedAt) : '' })].filter(Boolean).join(' · ')}
                                                    </dd>
                                                </div>
                                            )}
                                        </dl>
                                    </CardContent>
                                    <CardFooter className="pt-0">
                                        <Button variant="outline" size="sm" className="w-full" onClick={() => handleEdit(s)}>
                                            <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                            {t('common.edit', 'Edit')}
                                        </Button>
                                    </CardFooter>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="table" className="m-0">
                    <DataTable
                        columns={columns}
                        data={scenarios}
                        searchKey="name"
                        searchPlaceholder={t('recoveryScenarios.searchPlaceholder', 'Search scenarios...')}
                    />
                </TabsContent>
            </Tabs>

            <EstatePartsDialog open={showParts} onOpenChange={setShowParts} />
            <ScenarioFormDialog
                open={showForm}
                onOpenChange={(open) => {
                    setShowForm(open)
                    if (!open) {
                        setSelectedScenario(null)
                    }
                }}
                scenario={selectedScenario}
            />
        </div>
    )
}
